import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save, Search, X } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useFetch, PageHeader, Loading, ErrorState, SectionCard, fmtDate, Paginator } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function ConfirmDialog({ open, title, body, onConfirm, onCancel }) {
  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader><AlertDialogTitle>{title}</AlertDialogTitle><AlertDialogDescription>{body}</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid="confirm-ok-btn">Confirm</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const pctOk = (v) => v !== "" && !Number.isNaN(Number(v)) && Number(v) >= 0 && Number(v) <= 100;

function DefaultsCard({ data, onSaved }) {
  const [shop, setShop] = useState(""); const [partner, setPartner] = useState(""); const [ask, setAsk] = useState(false);
  useEffect(() => { setShop(String(data.commission?.shop_commission_percent ?? "")); setPartner(String(data.commission?.delivery_partner_percent ?? "")); }, [data]);
  const reset = () => { setShop(String(data.commission?.shop_commission_percent ?? "")); setPartner(String(data.commission?.delivery_partner_percent ?? "")); };
  const start = () => { if (!pctOk(shop) || !pctOk(partner)) { toast.error("Percent must be between 0 and 100"); return; } setAsk(true); };
  const save = async () => {
    setAsk(false);
    try { await api.put("/admin/commission/defaults", { shop_commission_percent: Number(shop), delivery_partner_percent: Number(partner), confirm: true }); toast.success("Default rates saved"); onSaved(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <SectionCard className="p-5" data-testid="commission-defaults">
      <h3 className="font-semibold font-outfit mb-1">Default rates</h3>
      <p className="text-xs text-muted-foreground mb-4">Shop commission is charged on item value after discounts, excluding delivery, platform fee and GST. Each order keeps a snapshot of the rate used.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs">Default shop commission %<Input type="number" value={shop} onChange={(e) => setShop(e.target.value)} className="mt-1" data-testid="default-shop-commission-input" /></label>
        <label className="text-xs">Delivery partner payout share %<Input type="number" value={partner} onChange={(e) => setPartner(e.target.value)} className="mt-1" data-testid="delivery-partner-share-input" /></label>
      </div>
      <div className="flex gap-2 mt-4"><Button size="sm" onClick={start} data-testid="save-defaults-btn"><Save className="h-3.5 w-3.5 mr-1.5" />Save</Button><Button size="sm" variant="outline" onClick={reset} data-testid="cancel-defaults-btn">Cancel</Button></div>
      <ConfirmDialog open={ask} title="Change default rates?" body={`Shop commission ${shop}% and delivery partner share ${partner}% will apply to new orders only.`} onConfirm={save} onCancel={() => setAsk(false)} />
    </SectionCard>
  );
}

function PlatformFeeCard({ pf, onSaved }) {
  const [p, setP] = useState(pf || {}); const [ask, setAsk] = useState(false);
  useEffect(() => { setP(pf || {}); }, [pf]);
  const start = () => {
    if (Number(p.max) < Number(p.min) || Number(p.max) > 9 || Number(p.min) < 0 || Number(p.threshold) < 0) { toast.error("Check values: 0 ≤ min ≤ max ≤ ₹9, threshold ≥ 0"); return; }
    setAsk(true);
  };
  const save = async () => {
    setAsk(false);
    try { await api.put("/admin/settings", { platform_fee_enabled: !!p.enabled, platform_fee_min: Number(p.min), platform_fee_max: Number(p.max), platform_fee_threshold: Number(p.threshold) }); toast.success("Platform fee saved"); onSaved(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <SectionCard className="p-5" data-testid="commission-platform-fee">
      <h3 className="font-semibold font-outfit mb-4">Platform / service fee</h3>
      <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={!!p.enabled} onChange={(e) => setP({ ...p, enabled: e.target.checked })} data-testid="cm-pf-enabled" />Enabled</label>
      <div className="grid grid-cols-3 gap-3">
        {[["min", "Min ₹"], ["max", "Max ₹"], ["threshold", "Threshold ₹"]].map(([k, l]) => (
          <label key={k} className="text-xs">{l}<Input type="number" value={p[k] ?? ""} onChange={(e) => setP({ ...p, [k]: e.target.value })} className="mt-1" data-testid={`cm-pf-${k}`} /></label>
        ))}
      </div>
      <div className="flex gap-2 mt-4"><Button size="sm" onClick={start} data-testid="save-pf-btn"><Save className="h-3.5 w-3.5 mr-1.5" />Save</Button><Button size="sm" variant="outline" onClick={() => setP(pf || {})} data-testid="cancel-pf-btn">Cancel</Button></div>
      <ConfirmDialog open={ask} title="Change platform fee?" body="New fee settings apply to new orders only." onConfirm={save} onCancel={() => setAsk(false)} />
    </SectionCard>
  );
}

function ShopRow({ s, onSaved }) {
  const [v, setV] = useState(s.custom_percent ?? ""); const [ask, setAsk] = useState(null);
  const save = async (val) => {
    setAsk(null);
    try { await api.put(`/admin/commission/shops/${s.id}`, { commission_percent: val, confirm: true }); toast.success("Shop rate saved"); onSaved(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const start = () => { if (!pctOk(v)) { toast.error("Percent must be between 0 and 100"); return; } setAsk({ val: Number(v) }); };
  return (
    <tr className="border-t" data-testid={`shop-commission-row-${s.id}`}>
      <td className="py-2 pr-3 text-sm">{s.name}<div className="text-xs text-muted-foreground">{s.status}</div></td>
      <td className="py-2 pr-3 text-sm font-semibold" data-testid={`effective-rate-${s.id}`}>{s.effective.percent}% <Badge variant={s.effective.source === "custom" ? "default" : "secondary"} className="ml-1">{s.effective.source === "custom" ? "Custom" : "Default"}</Badge></td>
      <td className="py-2 pr-3"><Input type="number" value={v} placeholder="Use default" onChange={(e) => setV(e.target.value)} className="h-8 w-28" data-testid={`shop-rate-input-${s.id}`} /></td>
      <td className="py-2 flex gap-1">
        <Button size="sm" onClick={start} data-testid={`shop-rate-save-${s.id}`}>Save</Button>
        {s.custom_percent != null && <Button size="sm" variant="outline" onClick={() => setAsk({ val: null })} data-testid={`shop-rate-clear-${s.id}`}><X className="h-3.5 w-3.5" /></Button>}
      </td>
      <ConfirmDialog open={!!ask} title={`Change commission for ${s.name}?`} body={ask?.val == null ? "Remove the custom rate; the default rate will apply to new orders." : `Set ${ask?.val}% for new orders of this shop.`} onConfirm={() => save(ask.val)} onCancel={() => setAsk(null)} />
    </tr>
  );
}

function ShopsCard({ onChanged }) {
  const [q, setQ] = useState(""); const [search, setSearch] = useState(""); const [page, setPage] = useState(1);
  const { data, loading, error, refetch } = useFetch(`/admin/commission/shops?page=${page}&limit=20${search ? `&search=${encodeURIComponent(search)}` : ""}`, [page, search]);
  return (
    <SectionCard className="p-5 lg:col-span-2" data-testid="commission-shops">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-semibold font-outfit">Shop-wise commission</h3>
        <form onSubmit={(e) => { e.preventDefault(); setPage(1); setSearch(q); }} className="flex gap-2 w-full sm:w-auto"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search shop" className="h-8 w-full sm:w-56" data-testid="shop-commission-search-input" /><Button size="sm" type="submit" data-testid="shop-commission-search-btn"><Search className="h-3.5 w-3.5" /></Button></form>
      </div>
      {loading && !data ? <Loading /> : error && !data ? <ErrorState error={error} onRetry={refetch} /> : !data.items.length ? <p className="text-sm text-muted-foreground" data-testid="shop-commission-empty">No shops match your search.</p> : (
        <div className="overflow-x-auto"><table className="w-full"><thead><tr className="text-left text-xs text-muted-foreground"><th className="pb-2">Shop</th><th className="pb-2">Effective rate</th><th className="pb-2">Custom %</th><th /></tr></thead>
          <tbody>{data.items.map((s) => <ShopRow key={`${s.id}-${s.custom_percent}`} s={s} onSaved={() => { refetch(); onChanged(); }} />)}</tbody></table>
          <Paginator page={page} total={data.total} limit={20} onPage={setPage} /></div>
      )}
    </SectionCard>
  );
}

function HistoryCard({ items }) {
  return (
    <SectionCard className="p-5 lg:col-span-2" data-testid="commission-history">
      <h3 className="font-semibold font-outfit mb-3">Change history</h3>
      {!items.length ? <p className="text-sm text-muted-foreground">No changes yet.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-xs text-muted-foreground"><th>When</th><th>Admin</th><th>Shop</th><th>Field</th><th>Old → New</th></tr></thead>
          <tbody>{items.map((h) => (<tr key={h.id} className="border-t"><td className="py-1.5">{fmtDate(h.created_at)}</td><td>{h.admin_email}</td><td>{h.shop_name || "All shops (default)"}</td><td>{h.field}</td><td>{h.previous ?? "default"}{h.previous_source ? ` (${h.previous_source})` : ""}{h.previous != null && h.field !== "delivery_partner_percent" ? "%" : ""} → {h.new ?? "default"}{h.new_source ? ` (${h.new_source})` : ""}{h.new != null && h.field !== "delivery_partner_percent" ? "%" : ""}</td></tr>))}</tbody></table></div>
      )}
    </SectionCard>
  );
}

export default function CommissionPage() {
  const { data, loading, error, refetch } = useFetch("/admin/commission", []);
  if (loading && !data) return <div><PageHeader title="Commission Management" /><Loading /></div>;
  if (error && !data) return <div><PageHeader title="Commission Management" /><ErrorState error={error} onRetry={refetch} /></div>;
  return (
    <div data-testid="commission-page">
      <PageHeader title="Commission Management" subtitle="Default and shop-wise commission, delivery partner share and platform fee" />
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-2 [&>*]:min-w-0">
        <DefaultsCard data={data} onSaved={refetch} />
        <PlatformFeeCard pf={data.platform_fee} onSaved={refetch} />
        <ShopsCard onChanged={refetch} />
        <HistoryCard items={data.history || []} />
      </div>
    </div>
  );
}
